import type { WarpFn } from "./pathwarp";
import type { Rect } from "./matrix";

/**
 * Liquify (SPEC.md §5): a displacement field over a rectangular region of a node's local
 * (post-warp) space. Output point p shows the content found at p + d(p) — a backward map,
 * which is what an image shader needs. Vectors need the forward map (content point q lands
 * where p + d(p) = q); see `liquifyForward`.
 *
 * The field is a cols × rows grid of samples; cell (i, j) sits at
 * (x + (i + 0.5)·cw, y + (j + 0.5)·ch), matching GPU texel-centre sampling of the uploaded
 * field texture, and is bilinearly interpolated in between. Samples outside the grid clamp to
 * the edge, and brushes never touch the outermost ring, so the displacement fades to zero at
 * the region's border on both the CPU and the GPU.
 */

/** Brush tools. Twirl direction is a dab parameter (Alt reverses it, as in Photoshop). */
export const LIQUIFY_TOOLS = ["push", "twirl", "bloat", "pinch", "reconstruct", "freeze", "thaw"] as const;
export type LiquifyTool = (typeof LIQUIFY_TOOLS)[number];

/** Upper bound on samples per side (keeps documents small and per-dab uploads cheap; = schema LIQUIFY_MAX_CELLS). */
const LIQUIFY_MAX_CELLS = 384;

export interface LiquifyDab {
  tool: LiquifyTool;
  /** Centre and radius, in the field's (node-local) units. */
  cx: number;
  cy: number;
  radius: number;
  /** 0..1: how much one dab does at the brush centre. */
  strength: number;
  /** 0..1: edge hardness (0 = soft feathered edge, 1 = nearly flat to the rim). */
  density?: number;
  /** Push: pointer movement since the previous dab. */
  dx?: number;
  dy?: number;
  /** Twirl: +1 clockwise (on screen, y down), -1 counter-clockwise. */
  dir?: number;
}

export interface DirtyRect {
  i0: number;
  j0: number;
  i1: number;
  j1: number;
}

/** Stored form (schema `Liquify`): region, grid, quantisation step and encoded planes. */
export interface StoredLiquify {
  x: number;
  y: number;
  w: number;
  h: number;
  cols: number;
  rows: number;
  /** Local units per stored integer step of displacement. */
  step: number;
  /** base64 of the run-length/delta/varint-coded dx plane followed by the dy plane. */
  data: string;
  /** Freeze mask (0..255 per sample, same coding), or null when nothing is frozen. */
  freeze: string | null;
}

export class LiquifyField {
  readonly cw: number;
  readonly ch: number;
  /** dx, dy per sample, in local units, row by row. */
  data: Float32Array;
  /** 0 = free, 255 = fully frozen. */
  mask: Uint8Array;
  private scratch: Float32Array;

  constructor(
    readonly x: number,
    readonly y: number,
    readonly w: number,
    readonly h: number,
    readonly cols: number,
    readonly rows: number,
  ) {
    this.cw = w / cols;
    this.ch = h / rows;
    this.data = new Float32Array(cols * rows * 2);
    this.scratch = new Float32Array(this.data.length);
    this.mask = new Uint8Array(cols * rows);
  }

  /**
   * A fresh field over `box` (the node's visible local bounds), padded so content can be
   * pushed outward, with square cells no bigger than the grid limit allows.
   */
  static over(box: Rect, opts: { margin?: number; maxCells?: number } = {}): LiquifyField {
    const pad = (opts.margin ?? 0.25) * Math.max(box.w, box.h, 1);
    const w = Math.max(box.w, 1e-3) + pad * 2, h = Math.max(box.h, 1e-3) + pad * 2;
    const cell = Math.max(Math.max(w, h) / (opts.maxCells ?? LIQUIFY_MAX_CELLS), 1e-3);
    const cols = Math.min(LIQUIFY_MAX_CELLS, Math.max(4, Math.ceil(w / cell)));
    const rows = Math.min(LIQUIFY_MAX_CELLS, Math.max(4, Math.ceil(h / cell)));
    return new LiquifyField(box.x - pad, box.y - pad, cols * cell, rows * cell, cols, rows);
  }

  clone(): LiquifyField {
    const f = new LiquifyField(this.x, this.y, this.w, this.h, this.cols, this.rows);
    f.data.set(this.data);
    f.mask.set(this.mask);
    return f;
  }

  get region(): Rect {
    return { x: this.x, y: this.y, w: this.w, h: this.h };
  }

  reset() {
    this.data.fill(0);
  }

  /** Bilinear displacement at a local point. */
  sample(px: number, py: number, src = this.data): [number, number] {
    const { cols, rows } = this;
    const fx = Math.min(cols - 1, Math.max(0, (px - this.x) / this.cw - 0.5));
    const fy = Math.min(rows - 1, Math.max(0, (py - this.y) / this.ch - 0.5));
    const x0 = Math.floor(fx), y0 = Math.floor(fy);
    const x1 = Math.min(cols - 1, x0 + 1), y1 = Math.min(rows - 1, y0 + 1);
    const tx = fx - x0, ty = fy - y0;
    const i00 = (y0 * cols + x0) * 2, i10 = (y0 * cols + x1) * 2, i01 = (y1 * cols + x0) * 2, i11 = (y1 * cols + x1) * 2;
    const a = (1 - tx) * (1 - ty), b = tx * (1 - ty), c = (1 - tx) * ty, d = tx * ty;
    return [a * src[i00] + b * src[i10] + c * src[i01] + d * src[i11], a * src[i00 + 1] + b * src[i10 + 1] + c * src[i01 + 1] + d * src[i11 + 1]];
  }

  /** Displacement and its Jacobian [∂dx/∂x, ∂dx/∂y, ∂dy/∂x, ∂dy/∂y] at a local point. */
  sampleJ(px: number, py: number): { dx: number; dy: number; j: [number, number, number, number] } {
    const { cols, rows, data: s } = this;
    const ux = (px - this.x) / this.cw - 0.5, uy = (py - this.y) / this.ch - 0.5;
    const fx = Math.min(cols - 1, Math.max(0, ux)), fy = Math.min(rows - 1, Math.max(0, uy));
    const x0 = Math.min(cols - 2, Math.floor(fx)), y0 = Math.min(rows - 2, Math.floor(fy));
    const tx = fx - x0, ty = fy - y0;
    const i00 = (y0 * cols + x0) * 2, i10 = i00 + 2, i01 = i00 + cols * 2, i11 = i01 + 2;
    const a = (1 - tx) * (1 - ty), b = tx * (1 - ty), c = (1 - tx) * ty, d = tx * ty;
    // Clamped axes have zero slope.
    const kx = ux < 0 || ux > cols - 1 ? 0 : 1 / this.cw, ky = uy < 0 || uy > rows - 1 ? 0 : 1 / this.ch;
    const j: [number, number, number, number] = [0, 0, 0, 0];
    for (let k = 0; k < 2; k++) {
      const v00 = s[i00 + k], v10 = s[i10 + k], v01 = s[i01 + k], v11 = s[i11 + k];
      j[k * 2] = ((1 - ty) * (v10 - v00) + ty * (v11 - v01)) * kx;
      j[k * 2 + 1] = ((1 - tx) * (v01 - v00) + tx * (v11 - v10)) * ky;
    }
    return { dx: a * s[i00] + b * s[i10] + c * s[i01] + d * s[i11], dy: a * s[i00 + 1] + b * s[i10 + 1] + c * s[i01 + 1] + d * s[i11 + 1], j };
  }

  /**
   * Apply one brush dab. Every tool is a lookup map q(p) — the new output at p is the current
   * output at q(p) — so the new field is d'(p) = q − p + d(q), which composes strokes exactly
   * instead of just adding vectors (spikes/src/liquify/field.ts). Frozen samples keep their
   * value. Returns the touched samples, or null if the dab missed the field.
   */
  dab(b: LiquifyDab): DirtyRect | null {
    const { cols, rows, cw, ch } = this;
    const r = b.radius;
    if (!(r > 0) || !(b.strength > 0)) return null;
    // Never touch the outer ring: displacement must fade to zero at the region's edge.
    const i0 = Math.max(1, Math.floor((b.cx - r - this.x) / cw - 0.5));
    const i1 = Math.min(cols - 2, Math.ceil((b.cx + r - this.x) / cw - 0.5));
    const j0 = Math.max(1, Math.floor((b.cy - r - this.y) / ch - 0.5));
    const j1 = Math.min(rows - 2, Math.ceil((b.cy + r - this.y) / ch - 0.5));
    if (i0 > i1 || j0 > j1) return null;
    const old = this.data;
    const next = this.scratch;
    const mask = this.mask;
    const s = Math.min(1, b.strength);
    const k = falloffExponent(b.density ?? 0.5);
    const r2 = r * r;
    const twirl = (b.dir ?? 1) * s * 0.2;
    const pdx = b.dx ?? 0, pdy = b.dy ?? 0;
    for (let j = j0; j <= j1; j++) {
      const row = j * cols;
      // Copy the touched span first; untouched samples in it keep their value.
      next.set(old.subarray((row + i0) * 2, (row + i1 + 1) * 2), (row + i0) * 2);
      for (let i = i0; i <= i1; i++) {
        const px = this.x + (i + 0.5) * cw, py = this.y + (j + 0.5) * ch;
        const rx = px - b.cx, ry = py - b.cy;
        const d2 = rx * rx + ry * ry;
        if (d2 >= r2) continue;
        const w = (1 - d2 / r2) ** k;
        const m = row + i, o = m * 2;
        if (b.tool === "freeze" || b.tool === "thaw") {
          const v = mask[m] + (b.tool === "freeze" ? 1 : -1) * 255 * s * w;
          mask[m] = Math.max(0, Math.min(255, Math.round(v)));
          continue;
        }
        const free = 1 - mask[m] / 255;
        if (free <= 0) continue;
        const ww = w * free;
        if (b.tool === "reconstruct") {
          const keep = 1 - 0.3 * s * ww;
          next[o] = old[o] * keep;
          next[o + 1] = old[o + 1] * keep;
          continue;
        }
        let qx = px, qy = py;
        switch (b.tool) {
          case "push":
            qx = px - pdx * ww * s;
            qy = py - pdy * ww * s;
            break;
          case "twirl": {
            const a = -twirl * ww;
            const c = Math.cos(a), sn = Math.sin(a);
            qx = b.cx + rx * c - ry * sn;
            qy = b.cy + rx * sn + ry * c;
            break;
          }
          case "bloat":
            qx = b.cx + rx * (1 - 0.12 * s * ww);
            qy = b.cy + ry * (1 - 0.12 * s * ww);
            break;
          case "pinch":
            qx = b.cx + rx * (1 + 0.12 * s * ww);
            qy = b.cy + ry * (1 + 0.12 * s * ww);
            break;
        }
        const [ox, oy] = this.sample(qx, qy, old);
        next[o] = qx - px + ox;
        next[o + 1] = qy - py + oy;
      }
    }
    if (b.tool !== "freeze" && b.tool !== "thaw") {
      // Write the touched span back (old stays the canonical buffer).
      for (let j = j0; j <= j1; j++) {
        const a = (j * cols + i0) * 2, e = (j * cols + i1 + 1) * 2;
        old.set(next.subarray(a, e), a);
      }
    }
    return { i0, j0, i1, j1 };
  }

  /** Largest |dx| or |dy| (for choosing a texture encoding range). */
  maxMagnitude(): number {
    let m = 0;
    const d = this.data;
    for (let i = 0; i < d.length; i++) {
      const v = Math.abs(d[i]);
      if (v > m) m = v;
    }
    return m;
  }

  isIdentity(eps = 1e-4): boolean {
    return this.maxMagnitude() <= eps;
  }

  hasFreeze(): boolean {
    for (let i = 0; i < this.mask.length; i++) if (this.mask[i]) return true;
    return false;
  }

  /**
   * Bounds (local units) of samples with any displacement, padded by the displacement
   * reach, or null when the field is the identity. Points outside never move.
   */
  activeBounds(eps = 1e-4): Rect | null {
    const { cols, rows, data } = this;
    let i0 = cols, j0 = rows, i1 = -1, j1 = -1, reach = 0;
    for (let j = 0; j < rows; j++)
      for (let i = 0; i < cols; i++) {
        const o = (j * cols + i) * 2;
        const m = Math.max(Math.abs(data[o]), Math.abs(data[o + 1]));
        if (m <= eps) continue;
        if (i < i0) i0 = i;
        if (i > i1) i1 = i;
        if (j < j0) j0 = j;
        if (j > j1) j1 = j;
        if (m > reach) reach = m;
      }
    if (i1 < 0) return null;
    // A sample influences the cell on either side of it.
    const x0 = this.x + (i0 - 0.5) * this.cw - reach, y0 = this.y + (j0 - 0.5) * this.ch - reach;
    const x1 = this.x + (i1 + 1.5) * this.cw + reach, y1 = this.y + (j1 + 1.5) * this.ch + reach;
    return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
  }

  /**
   * Where the content of `content` (a local rect: the node's unliquified bounds) ends up:
   * the rect itself grown by every displaced sample whose source lies inside it.
   * Over-approximates when content is pushed inward (the frame doesn't shrink). `visible`
   * says whether content at a local point is shown at all (e.g. not hidden by a mask).
   */
  bounds(content: Rect, visible?: (x: number, y: number) => boolean): Rect {
    const { cols, rows, data, cw, ch } = this;
    let x0 = content.x, y0 = content.y, x1 = content.x + content.w, y1 = content.y + content.h;
    const cx1 = x1, cy1 = y1;
    for (let j = 0; j < rows; j++)
      for (let i = 0; i < cols; i++) {
        const o = (j * cols + i) * 2;
        const dx = data[o], dy = data[o + 1];
        if (Math.abs(dx) <= 1e-4 && Math.abs(dy) <= 1e-4) continue;
        const px = this.x + (i + 0.5) * cw, py = this.y + (j + 0.5) * ch;
        const sx = px + dx, sy = py + dy;
        if (sx < content.x || sy < content.y || sx > cx1 || sy > cy1) continue;
        // Content a mask hides doesn't count (a cut-out's removed background pushed outward).
        if (visible && !visible(sx, sy)) continue;
        if (px - cw / 2 < x0) x0 = px - cw / 2;
        if (py - ch / 2 < y0) y0 = py - ch / 2;
        if (px + cw / 2 > x1) x1 = px + cw / 2;
        if (py + ch / 2 > y1) y1 = py + ch / 2;
      }
    return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
  }

  /** The same field for the node scaled by `k` about its local origin. */
  scaled(k: number): LiquifyField {
    const f = new LiquifyField(this.x * k, this.y * k, this.w * k, this.h * k, this.cols, this.rows);
    for (let i = 0; i < this.data.length; i++) f.data[i] = this.data[i] * k;
    f.mask.set(this.mask);
    return f;
  }
}

/** Brush profile (1 − t²)^k: density 0 → k = 3 (soft), 1 → k = 0.35 (hard edge). */
export function falloffExponent(density: number): number {
  const d = Math.min(1, Math.max(0, density));
  return 3 - 2.65 * d;
}

// ------------------------------------------------------------------ forward map (vectors)

/**
 * The forward map: a content point q (local, post-warp) moves to the p with p + d(p) = q.
 * Solved by fixed-point iteration (converges while the field doesn't fold, |∇d| < 1); the
 * Jacobian is (I + ∇d(p))⁻¹, so re-fitted curves keep their tangents.
 */
export function liquifyForward(f: LiquifyField, active = f.activeBounds()): WarpFn {
  return (qx, qy) => {
    if (!active || qx < active.x || qy < active.y || qx > active.x + active.w || qy > active.y + active.h) return { x: qx, y: qy, j: [1, 0, 0, 1] };
    let [dx, dy] = f.sample(qx, qy);
    let px = qx - dx, py = qy - dy;
    for (let it = 0; it < 12; it++) {
      [dx, dy] = f.sample(px, py);
      const nx = qx - dx, ny = qy - dy;
      const moved = Math.abs(nx - px) + Math.abs(ny - py);
      // Damped update keeps strong twirls from oscillating.
      px = it < 6 ? nx : (px + nx) / 2;
      py = it < 6 ? ny : (py + ny) / 2;
      if (moved < 1e-4) break;
    }
    const s = f.sampleJ(px, py);
    const a = 1 + s.j[0], b = s.j[1], c = s.j[2], d = 1 + s.j[3];
    const det = a * d - b * c;
    const j: [number, number, number, number] = Math.abs(det) > 1e-9 ? [d / det, -b / det, -c / det, a / det] : [1, 0, 0, 1];
    return { x: px, y: py, j };
  };
}

/** `outer ∘ inner` for warp functions, with the chain-ruled Jacobian. */
export function composeWarps(inner: WarpFn, outer: WarpFn): WarpFn {
  return (x, y) => {
    const a = inner(x, y);
    const b = outer(a.x, a.y);
    const [p, q, r, s] = b.j, [e, f, g, h] = a.j;
    return { x: b.x, y: b.y, j: [p * e + q * g, p * f + q * h, r * e + s * g, r * f + s * h] };
  };
}

// ------------------------------------------------------------------ serialisation

/**
 * Field → stored form. Displacements are quantised to `step` (a power of two, ≤ 1/128 unit
 * unless the field is huge) and each plane is delta-coded in raster order, zero runs
 * collapsed, as zig-zag varints, then base64. Untouched areas cost almost nothing.
 */
export function encodeLiquify(f: LiquifyField): StoredLiquify {
  const n = f.cols * f.rows;
  const max = f.maxMagnitude();
  const step = Math.max(1 / 128, 2 ** Math.ceil(Math.log2(Math.max(max / 32000, 1e-12))));
  const q = new Int32Array(n * 2);
  for (let i = 0; i < n; i++) {
    q[i] = Math.round(f.data[i * 2] / step);
    q[n + i] = Math.round(f.data[i * 2 + 1] / step);
  }
  return {
    x: round6(f.x),
    y: round6(f.y),
    w: round6(f.w),
    h: round6(f.h),
    cols: f.cols,
    rows: f.rows,
    step,
    data: toBase64(encodeInts(q, [0, n])),
    freeze: f.hasFreeze() ? toBase64(encodeInts(Int32Array.from(f.mask), [0])) : null,
  };
}

/** Stored form → field. Throws on malformed data. */
export function decodeLiquify(s: StoredLiquify): LiquifyField {
  const f = new LiquifyField(s.x, s.y, s.w, s.h, s.cols, s.rows);
  const n = s.cols * s.rows;
  const q = decodeInts(fromBase64(s.data), n * 2, [0, n]);
  for (let i = 0; i < n; i++) {
    f.data[i * 2] = q[i] * s.step;
    f.data[i * 2 + 1] = q[n + i] * s.step;
  }
  if (s.freeze) {
    const m = decodeInts(fromBase64(s.freeze), n, [0]);
    for (let i = 0; i < n; i++) f.mask[i] = Math.max(0, Math.min(255, m[i]));
  }
  return f;
}

const decoded = new WeakMap<object, LiquifyField | null>();

/** Cached decode by stored-object identity (documents replace it on change); null if malformed. */
export function liquifyField(s: StoredLiquify): LiquifyField | null {
  if (decoded.has(s)) return decoded.get(s)!;
  let f: LiquifyField | null = null;
  try {
    f = decodeLiquify(s);
  } catch {
    f = null;
  }
  decoded.set(s, f);
  return f;
}

/** A stored field for the node scaled uniformly by `k` (no re-encoding needed). */
export function scaleStoredLiquify<T extends StoredLiquify>(s: T, k: number): T {
  return { ...s, x: s.x * k, y: s.y * k, w: s.w * k, h: s.h * k, step: s.step * k };
}

const boundsCache = new WeakMap<object, { content: string; r: Rect }>();

/**
 * Local bounds of the liquified content (see LiquifyField.bounds), cached per stored field.
 * `visible` (identified by `visibleKey`) limits it to content that is shown.
 */
export function liquifyBounds(s: StoredLiquify, content: Rect, visible?: { key: string; at: (x: number, y: number) => boolean } | null): Rect {
  const key = `${content.x},${content.y},${content.w},${content.h}|${visible?.key ?? ""}`;
  const hit = boundsCache.get(s);
  if (hit?.content === key) return hit.r;
  const f = liquifyField(s);
  const r = f ? f.bounds(content, visible?.at) : content;
  boundsCache.set(s, { content: key, r });
  return r;
}

const round6 = (v: number) => Math.round(v * 1e6) / 1e6;

/**
 * Delta + zero-run + zig-zag varint coding. `starts` lists indices where the running value
 * resets to 0 (plane boundaries). Token = (zigzag(delta) << 1) for a literal or
 * (runLength << 1 | 1) for a run of zero deltas.
 */
export function encodeInts(v: ArrayLike<number>, starts: number[] = [0]): Uint8Array {
  const out: number[] = [];
  const put = (x: number) => {
    // x ≥ 0, may exceed 2³¹: use arithmetic, not bit ops.
    while (x >= 128) {
      out.push((x % 128) | 128);
      x = Math.floor(x / 128);
    }
    out.push(x);
  };
  let prev = 0, run = 0;
  const flush = () => {
    if (run) put(run * 2 + 1);
    run = 0;
  };
  const resets = new Set(starts);
  for (let i = 0; i < v.length; i++) {
    if (resets.has(i)) {
      flush();
      prev = 0;
    }
    const d = v[i] - prev;
    prev = v[i];
    if (d === 0) {
      run++;
      continue;
    }
    flush();
    put((d < 0 ? -2 * d - 1 : 2 * d) * 2);
  }
  flush();
  return Uint8Array.from(out);
}

export function decodeInts(bytes: Uint8Array, count: number, starts: number[] = [0]): Int32Array {
  const out = new Int32Array(count);
  const resets = new Set(starts);
  let pos = 0, i = 0, prev = 0;
  const get = () => {
    let x = 0, mul = 1;
    for (;;) {
      if (pos >= bytes.length) throw new Error("liquify data truncated");
      const b = bytes[pos++];
      x += (b & 127) * mul;
      if (b < 128) return x;
      mul *= 128;
      if (mul > 2 ** 49) throw new Error("liquify varint overflow");
    }
  };
  while (i < count) {
    const t = get();
    if (t % 2 === 1) {
      const run = (t - 1) / 2;
      if (i + run > count) throw new Error("liquify run overflows the grid");
      for (let k = 0; k < run; k++, i++) {
        if (resets.has(i)) prev = 0;
        out[i] = prev;
      }
    } else {
      if (resets.has(i)) prev = 0;
      const z = t / 2;
      prev += z % 2 ? -(z + 1) / 2 : z / 2;
      out[i++] = prev;
    }
  }
  if (pos !== bytes.length) throw new Error("liquify data has trailing bytes");
  return out;
}

function toBase64(bytes: Uint8Array): string {
  let s = "";
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(s);
}

function fromBase64(s: string): Uint8Array {
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}
